import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SecurityIcon from "@mui/icons-material/Security";
import LockIcon from "@mui/icons-material/Lock";
import LayersIcon from "@mui/icons-material/Layers";
import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";

// ✅ Cyber Security Content Data
const cyberSecurityContent = [
  {
    section: "Cybersecurity Basics",
    icon: <SecurityIcon />,
    items: [
      {
        name: "Password Security",
        description: "Strong password policies & hashing techniques.",
        fullContent: [
          {
            heading: "Overview",
            text: "Passwords should be stored using hashing algorithms like bcrypt or SHA-256 to prevent leakage.",
          },
          {
            heading: "Example (Python - bcrypt hashing)",
            code: `import bcrypt

password = b"SuperSecret123"
hashed = bcrypt.hashpw(password, bcrypt.gensalt())
print("Hashed:", hashed)

# Verify
if bcrypt.checkpw(password, hashed):
    print("Password Match!")`,
            language: "python",
          },
        ],
      },
      {
        name: "Basic Encryption",
        description: "Using symmetric key encryption (AES).",
        fullContent: [
          {
            heading: "Example (Python - AES encryption)",
            code: `from Crypto.Cipher import AES
import os

key = os.urandom(16)
cipher = AES.new(key, AES.MODE_EAX)
nonce = cipher.nonce
ciphertext, tag = cipher.encrypt_and_digest(b"My secret data")

print("Ciphertext:", ciphertext)`,
            language: "python",
          },
        ],
      },
    ],
  },
  {
    section: "Intermediate Security",
    icon: <LockIcon />,
    items: [
      {
        name: "Penetration Testing",
        description: "Identify vulnerabilities using ethical hacking tools.",
        fullContent: [
          {
            heading: "Example (Python - Simple Port Scanner)",
            code: `import socket

target = "127.0.0.1"
for port in range(20, 100):
    sock = socket.socket(socket.AF_INET, socket.SOCK_STREAM)
    result = sock.connect_ex((target, port))
    if result == 0:
        print(f"Port {port} is open")
    sock.close()`,
            language: "python",
          },
        ],
      },
      {
        name: "Secure Coding",
        description: "Avoid common vulnerabilities (SQL Injection, XSS).",
        fullContent: [
          {
            heading: "Example (Node.js - Safe SQL Query)",
            code: `import mysql from "mysql2/promise";

const connection = await mysql.createConnection({host:"localhost", user:"root", database:"test"});
const [rows] = await connection.execute("SELECT * FROM users WHERE email = ?", ["test@example.com"]);
console.log(rows);`,
            language: "javascript",
          },
        ],
      },
    ],
  },
  {
    section: "Advanced Security",
    icon: <LayersIcon />,
    items: [
      {
        name: "Malware Analysis",
        description: "Analyze suspicious files and behavior.",
        fullContent: [
          {
            heading: "Example (Python - File Hashing for Malware Check)",
            code: `import hashlib

with open("suspicious.exe", "rb") as f:
    file_hash = hashlib.sha256(f.read()).hexdigest()
print("File Hash:", file_hash)`,
            language: "python",
          },
        ],
      },
      {
        name: "Zero Trust Security",
        description: "Never trust, always verify approach.",
        fullContent: [
          {
            heading: "Example (Node.js - Token Verification)",
            code: `import jwt from "jsonwebtoken";

const token = jwt.sign({ userId: 1 }, "secretKey");
try {
  const decoded = jwt.verify(token, "secretKey");
  console.log("Verified:", decoded);
} catch (err) {
  console.log("Invalid Token");
}`,
            language: "javascript",
          },
        ],
      },
      {
        name: "Wireshark (Packet Analysis)",
        description: "Monitor network traffic and capture packets.",
        fullContent: [
          {
            heading: "Example (Shell - tcpdump capture for Wireshark)",
            code: `# Capture packets on eth0 and save to file
tcpdump -i eth0 -w capture.pcap

# Open capture.pcap in Wireshark GUI`,
            language: "bash",
          },
        ],
      },
      {
        name: "Metasploit Framework",
        description: "Exploitation & vulnerability testing tool.",
        fullContent: [
          {
            heading: "Example (Metasploit Console)",
            code: `# Start Metasploit
msfconsole

# Search for exploits
search vsftpd

# Use exploit
use exploit/unix/ftp/vsftpd_234_backdoor
set RHOST 192.168.1.10
exploit`,
            language: "bash",
          },
        ],
      },
      {
        name: "Snort IDS",
        description: "Intrusion Detection & Prevention System.",
        fullContent: [
          {
            heading: "Example (Snort Rule - detect ICMP ping)",
            code: `alert icmp any any -> any any (msg:"ICMP Ping Detected"; sid:1000001;)`,
            language: "bash",
          },
          {
            heading: "Command to run Snort",
            code: `snort -c /etc/snort/snort.conf -i eth0`,
            language: "bash",
          },
        ],
      },
      {
        name: "Splunk (SOC Automation)",
        description: "Security Information & Event Management (SIEM).",
        fullContent: [
          {
            heading: "Example (Splunk Search Query - Failed Logins)",
            code: `index=security sourcetype=linux_secure "Failed password"
| stats count by user, src_ip
| sort -count`,
            language: "sql",
          },
          {
            heading: "Python (Splunk API Query)",
            code: `import splunklib.client as client

service = client.connect(host="localhost", port=8089, username="admin", password="changeme")
for app in service.apps:
    print(app.name)`,
            language: "python",
          },
        ],
      },
    ],
  },
];

function CyberSecurity() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Cyber Security
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {cyberSecurityContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {cyberSecurityContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {cyberSecurityContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {cyberSecurityContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.points && (
                                <ul style={{ marginLeft: "1.5rem", marginTop: 0, marginBottom: '16px', color: '#4a5568' }}>
                                    {content.points.map((point, pIdx) => (
                                        <li key={pIdx}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language={content.language || "javascript"}
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default CyberSecurity;
